// Module-level config holder. The plugin's install() hook seeds this so
// non-component callers (stores, composables, etc.) can read the apiBaseUrl
// and per-request headers without going through Vue's inject() chain.
import { useBridgeStore } from '../stores/bridge.js'
import { ensureFreshToken } from './tokenProvider.js'

let activeConfig = {
  apiBaseUrl: '/api',
  credentials: 'include',
  headers: null,
}

export function setApiConfig(cfg) {
  activeConfig = { ...activeConfig, ...cfg }
}

/**
 * RDrive credential headers for the harness. Every session-bound endpoint
 * (chat, query, files, transcribe, session detail/rating/title, tools) checks
 * that the caller's scoped token resolves to the session's owner, so the
 * token, API host and project ride along on every request as headers —
 * never in the URL, where proxies and browser history would record them.
 */
function credentialHeaders() {
  let bridge
  try {
    bridge = useBridgeStore()
  } catch {
    return {} // pinia not active yet (plugin boot) — nothing to attach
  }
  const headers = {}
  if (bridge.scopedToken) headers['Authorization'] = `Bearer ${bridge.scopedToken}`
  if (bridge.apiUrl) headers['X-RDrive-Api-Url'] = bridge.apiUrl
  if (bridge.projectCode) headers['X-RDrive-Project'] = bridge.projectCode
  return headers
}

function buildHeaders(includeJson) {
  const h = activeConfig.headers
  const extra = typeof h === 'function' ? (h() || {}) : (h || {})
  const headers = { ...credentialHeaders(), ...extra }
  if (includeJson) headers['Content-Type'] = 'application/json'
  return headers
}

async function init(method, body) {
  await ensureFreshToken()
  const i = {
    method,
    credentials: activeConfig.credentials ?? 'include',
    headers: buildHeaders(body !== undefined),
  }
  if (body !== undefined) i.body = JSON.stringify(body)
  return i
}

// Errors carry the HTTP status (err.status) so callers can react to specific
// codes — e.g. the token provider retries once on 401 with a fresh token.
function httpError(message, status) {
  const err = new Error(message)
  err.status = status
  return err
}

export async function get(path) {
  const res = await fetch(`${activeConfig.apiBaseUrl}${path}`, await init('GET'))
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw httpError(body.error || `GET ${path} failed: ${res.status}`, res.status)
  }
  return res.json()
}

export async function post(path, body) {
  const res = await fetch(`${activeConfig.apiBaseUrl}${path}`, await init('POST', body))
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw httpError(data.error || `POST ${path} failed: ${res.status}`, res.status)
  }
  return res.json()
}

export async function put(path, body) {
  const res = await fetch(`${activeConfig.apiBaseUrl}${path}`, await init('PUT', body))
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw httpError(data.error || `PUT ${path} failed: ${res.status}`, res.status)
  }
  return res.json()
}

export async function del(path) {
  const res = await fetch(`${activeConfig.apiBaseUrl}${path}`, await init('DELETE'))
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw httpError(body.error || `DELETE ${path} failed: ${res.status}`, res.status)
  }
  return res.json()
}

/**
 * POST a multipart FormData body (file uploads). Uses the same auth headers as
 * the JSON helpers but deliberately omits Content-Type so the browser sets the
 * multipart boundary itself. Same error semantics as post().
 */
export async function postForm(path, formData) {
  await ensureFreshToken()
  const res = await fetch(`${activeConfig.apiBaseUrl}${path}`, {
    method: 'POST',
    credentials: activeConfig.credentials ?? 'include',
    headers: buildHeaders(false),
    body: formData,
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw httpError(data.error || `POST ${path} failed: ${res.status}`, res.status)
  }
  return res.json()
}

/**
 * Resolve an API path to a URL usable for fetch or direct navigation.
 * `pathOrUrl` may be a full URL, an absolute `/api/...` path (as returned in a
 * download link event — the leading `/api` is folded onto apiBaseUrl so it
 * works whether the base is `/api` or a full host), or a bare API path.
 */
export function resolveApiUrl(pathOrUrl) {
  return /^https?:\/\//.test(pathOrUrl)
    ? pathOrUrl
    : pathOrUrl.startsWith('/api')
      ? `${activeConfig.apiBaseUrl}${pathOrUrl.slice(4)}`
      : `${activeConfig.apiBaseUrl}${pathOrUrl}`
}

/**
 * POST with SSE streaming. Returns the Response for stream reading.
 */
export async function postStream(path, body, signal) {
  const res = await fetch(`${activeConfig.apiBaseUrl}${path}`, {
    ...(await init('POST', body)),
    signal,
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw httpError(data.error || `POST ${path} stream failed: ${res.status}`, res.status)
  }
  return res
}
